/**
 * Production Retry Tracker
 * Retry management with Map-based storage and TTL
 *
 * Features:
 * - TTL-based automatic cleanup
 * - Memory limits with LRU eviction
 * - Performance monitoring
 * - Error boundaries
 * - Metrics collection
 */

import env from '@/config/env';

class ProductionRetryTracker {
  constructor(config = {}) {
    this.config = {
      maxEntries: config.maxEntries || 1000,
      ttl: config.ttl || 5 * 60 * 1000, // 5 minutes default
      cleanupInterval: config.cleanupInterval || 60 * 1000, // 1 minute
      maxRetries: config.maxRetries || env.MAX_RETRY_ATTEMPTS,
      enableMetrics: config.enableMetrics !== false,
      enableLogging: config.enableLogging !== false,
      warnThreshold: config.warnThreshold || 100, // Warn if > 100 active retries
    };

    this.registry = new Map();
    this.metrics = {
      totalRequests: 0,
      totalRetries: 0,
      successfulRetries: 0,
      failedRetries: 0,
      evictions: 0,
      timeouts: 0,
    };

    this.cleanupTimer = null;
    this.startCleanup();
  }

  /**
   * Generate unique string key for request tracking
   * Uses URL + method + timestamp + random for uniqueness
   */
  generateKey(url, options = {}) {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    const method = options.method || 'GET';

    return `${method}::${url}::${timestamp}::${random}`;
  }

  track(key, increment = true) {
    try {
      if (this.registry.size >= this.config.maxEntries) {
        this._evictOldest();
      }

      let entry = this.registry.get(key);

      if (!entry) {
        entry = {
          count: 0,
          firstAttempt: Date.now(),
          lastAttempt: Date.now(),
          url: key.split('::')[1] || 'unknown',
          method: key.split('::')[0] || 'unknown',
        };

        this.metrics.totalRequests++;
      }

      if (increment) {
        entry.count++;
        entry.lastAttempt = Date.now();
        this.metrics.totalRetries++;
      }

      this.registry.set(key, entry);

      return entry.count;
    } catch (error) {
      return 0;
    }
  }

  getCount(key) {
    const entry = this.registry.get(key);
    return entry ? entry.count : 0;
  }

  getEntry(key) {
    return this.registry.get(key) || null;
  }

  isMaxReached(key, customMax = null) {
    const count = this.getCount(key);
    const max = customMax || this.config.maxRetries;
    return count >= max;
  }

  markSuccess(key) {
    const entry = this.registry.get(key);
    if (entry && entry.count > 0) this.metrics.successfulRetries++;
    this.clear(key);
  }

  markFailed(key) {
    const entry = this.registry.get(key);
    if (entry && entry.count > 0) this.metrics.failedRetries++;
    this.clear(key);
  }

  clear(key) {
    this.registry.delete(key);
  }

  clearAll() {
    this.registry.clear();
  }

  _evictOldest() {
    if (this.registry.size === 0) return;

    // Find oldest entry by lastAttempt
    let oldestKey = null;
    let oldestTime = Date.now();

    for (const [key, entry] of this.registry.entries()) {
      if (entry.lastAttempt < oldestTime) {
        oldestTime = entry.lastAttempt;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.registry.delete(oldestKey);
      this.metrics.evictions++;
    }
  }

  cleanup() {
    const now = Date.now();
    const expired = [];

    for (const [key, entry] of this.registry.entries()) {
      const age = now - entry.lastAttempt;
      if (age > this.config.ttl) {
        expired.push(key);
        this.metrics.timeouts++;
      }
    }

    expired.forEach((key) => this.registry.delete(key));

    // Log metrics periodically
    if (this.config.enableMetrics && this.registry.size > 0) {
      this._logMetrics();
    }
  }

  startCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      try {
        this.cleanup();
      } catch (error) {}
    }, this.config.cleanupInterval);

    // Ensure cleanup doesn't prevent process termination
    if (this.cleanupTimer.unref) {
      this.cleanupTimer.unref();
    }
  }

  stopCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  getStats() {
    const entries = Array.from(this.registry.entries()).map(([key, entry]) => ({
      key: key.substring(0, 50) + '...',
      url: entry.url,
      method: entry.method,
      count: entry.count,
      age: Date.now() - entry.lastAttempt,
      duration: entry.lastAttempt - entry.firstAttempt,
    }));

    return {
      activeEntries: this.registry.size,
      metrics: { ...this.metrics },
      entries: entries.slice(0, 10), // Top 10 for debugging
      oldestEntry: entries.sort((a, b) => b.age - a.age)[0],
      mostRetried: entries.sort((a, b) => b.count - a.count)[0],
    };
  }

  getMetrics() {
    return {
      ...this.metrics,
      activeEntries: this.registry.size,
      memoryUsage: this._estimateMemoryUsage(),
      uptime: process.uptime ? process.uptime() : null,
    };
  }

  _estimateMemoryUsage() {
    return this.registry.size * 200;
  }

  _logMetrics() {}

  export() {
    return {
      config: this.config,
      metrics: this.metrics,
      entries: Array.from(this.registry.entries()).map(([key, entry]) => ({
        key,
        ...entry,
      })),
    };
  }

  import(state) {
    if (state.entries) {
      this.registry.clear();
      state.entries.forEach(({ key, ...entry }) => {
        this.registry.set(key, entry);
      });
    }
    if (state.metrics) {
      this.metrics = { ...state.metrics };
    }
  }

  destroy() {
    this.stopCleanup();
    this.registry.clear();
    this.metrics = {
      totalRequests: 0,
      totalRetries: 0,
      successfulRetries: 0,
      failedRetries: 0,
      evictions: 0,
      timeouts: 0,
    };
  }
}

// Singleton instance with prod defaults
const retryTracker = new ProductionRetryTracker({
  maxEntries: 1000,
  ttl: 5 * 60 * 1000, // 5 minutes
  cleanupInterval: 60 * 1000, // 1 minute
  maxRetries: env.MAX_RETRY_ATTEMPTS,
  enableMetrics: true,
  enableLogging: process.env.NODE_ENV !== 'production',
  warnThreshold: 100,
});

export default retryTracker;
export { ProductionRetryTracker };

// Development/debugging helpers
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__retryTracker = retryTracker;
  window.__retryStats = () => retryTracker.getStats();
  window.__retryMetrics = () => retryTracker.getMetrics();
  window.__retryClear = () => retryTracker.clearAll();
}
